import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { formatOpoNameWithCode, removeOpoCodePrefix } from '@features/components/CaseFormBlocks';
import { useDisclosure } from '@ds';
import { CreateOpoContactModal } from '@features/modals/CreateOpoContactModal';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import { InfoBanner } from '@ui';
import { useInfiniteOpoListQuery, useOpoByIdQuery } from '@/queries/opo';
import { getCountriesFilterByServiceRegion, renderAutocompleteItem } from '@/utils/helpers';
import { EUROPE_REGION_ID, ServiceRegionId } from '@/constants/serviceRegion.constants';
import { OpoOrganization, OpoOrganizationContact } from '@/api/apis/opo';
import { SearchQuery } from '@/api/types';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { OpoContactInputs } from './OpoContactInputs';
import { FormRow } from './FormRow';
import { CaseFacilityAutocomplete } from './CaseFacilityAutocomplete';

type OpoSelectorsProps = {
  disabled?: Partial<Record<keyof CaseFormType, boolean>>;
  showRequired: boolean;
  hasPermission?: boolean;
};

const useInfiniteOpoOrganizationAutocompleteQuery = (filters: SearchQuery) => {
  return useInfiniteOpoListQuery(filters);
};

export const OpoSelectors = memo(({ disabled, showRequired, hasPermission }: OpoSelectorsProps) => {
  const [contactsList, setContactsList] = useState<OpoOrganizationContact[]>([]);
  const { control, setValue, getValues } = useFormContext<CaseFormType>();

  const [opoId, opoContactId, secondaryOpoContactId, serviceRegionId, opoRequest] = useWatch({
    control,
    name: ['opoId', 'opoContactId', 'secondaryOpoContactId', 'serviceRegionId', 'opoRequest'],
  });

  const { data } = useOpoByIdQuery(opoId);

  const {
    isOpen: isOpoContactModalOpen,
    onClose: onOpoContactModalClose,
    onOpen: onOpoContactModalOpen,
  } = useDisclosure();

  const {
    isOpen: isSecondaryOpoContactModalOpen,
    onClose: onSecondaryOpoContactModalClose,
    onOpen: onSecondaryOpoContactModalOpen,
  } = useDisclosure();

  useEffect(() => {
    setContactsList(data?.contacts ?? []);
  }, [data]);

  const clearOpoContacts = useCallback(() => {
    setContactsList([]);
    setValue('secondaryOpoContactId', '');
    setValue('opoContactId', '');
    setValue('opoSourceContactPhone', '');
    setValue('secondaryOpoContactPhone', '');
  }, [setValue]);

  const handleOpoSelect = useCallback(
    (item?: OpoOrganization) => {
      if (!item?.contacts?.length) {
        clearOpoContacts();
        return;
      }

      const selectedPrimaryOpoContact = item.contacts.find((contact) => contact.isPrimary);

      setContactsList(item.contacts);
      setValue('opoContactId', selectedPrimaryOpoContact?.id ?? '');
      setValue('opoSourceContactPhone', selectedPrimaryOpoContact?.primaryPhone ?? '');
      setValue('secondaryOpoContactId', '');
      setValue('secondaryOpoContactPhone', '');
    },
    [clearOpoContacts, setValue],
  );

  const handleClearOpo = useCallback(() => {
    setValue('opoContactId', null);
    setValue('opoSourceContactPhone', '');
  }, [setValue]);

  const handleClearSecondaryOpo = useCallback(() => {
    setValue('secondaryOpoContactId', '');
    setValue('secondaryOpoContactPhone', '');
  }, [setValue]);

  const primaryContactsList = useMemo(
    () => contactsList.filter((contact) => contact.id !== secondaryOpoContactId),
    [secondaryOpoContactId, contactsList],
  );

  const secondaryContactsList = useMemo(
    () => contactsList.filter((contact) => contact.id !== opoContactId),
    [opoContactId, contactsList],
  );

  const regionId = serviceRegionId as ServiceRegionId;
  const opoNameWithCode = data ? formatOpoNameWithCode({ name: data.name, code: data.code }) : '';

  const { countriesByIso2 } = useCountriesData();

  const facilityFilters = useMemo(
    () => ({ countries: getCountriesFilterByServiceRegion(regionId, countriesByIso2) }),
    [regionId, countriesByIso2],
  );

  const showNoContactsBanner = !!opoId && !!data && contactsList.length === 0;

  const renderListItem = useCallback(
    (item: object) =>
      renderAutocompleteItem({
        name: formatOpoNameWithCode(item as { name: string; code?: string | null }),
        value: (item as { id: string }).id,
      }),
    [],
  );

  const handlePrimaryContactCreated = useCallback(
    (opoContact: OpoOrganizationContact) => {
      setValue('opoContactId', opoContact.id);
      setValue('opoSourceContactPhone', opoContact.primaryPhone ?? '');

      onOpoContactModalClose();
    },
    [onOpoContactModalClose, setValue],
  );

  const handleSecondaryContactCreated = useCallback(
    (opoContact: OpoOrganizationContact) => {
      setValue('secondaryOpoContactId', opoContact.id);
      setValue('secondaryOpoContactPhone', opoContact.primaryPhone ?? '');

      onSecondaryOpoContactModalClose();
    },
    [onSecondaryOpoContactModalClose, setValue],
  );

  return (
    <FormRow title="OPO" isRequired={showRequired && serviceRegionId !== EUROPE_REGION_ID} multiRow>
      <div className="w-full flex flex-col gap-3">
        <div className="flex">
          <div className="flex-[48.5%] grow-0">
            <CaseFacilityAutocomplete<CaseFormType, OpoOrganization>
              key={serviceRegionId}
              name="opoId"
              isDisabled={disabled?.opoId || !hasPermission}
              control={control}
              placeholder="Select OPO Name"
              ariaLabel="Select OPO Name"
              infiniteListQuery={useInfiniteOpoOrganizationAutocompleteQuery}
              renderListItem={renderListItem}
              onSelect={handleOpoSelect}
              defaultInputValue={opoNameWithCode}
              getDisplayName={formatOpoNameWithCode}
              formatSearchValue={removeOpoCodePrefix}
              filters={facilityFilters}
            />
          </div>
        </div>

        {showNoContactsBanner && (
          <InfoBanner iconType="warning">Selected OPO doesn’t have any contacts. You need to create one.</InfoBanner>
        )}

        <OpoContactInputs
          contactsList={primaryContactsList}
          disabledContact={disabled?.opoContactId || !hasPermission}
          disabledContactPhone={disabled?.opoSourceContactPhone || !hasPermission}
          opoId={opoId}
          contactFieldName="opoContactId"
          phoneFieldName="opoSourceContactPhone"
          contactFieldPlaceholder="OPO Contact"
          createNewItem={{
            title: 'Create OPO Contact',
            handler: onOpoContactModalOpen,
          }}
          onClear={handleClearOpo}
          isClearable={opoRequest}
        />

        <CreateOpoContactModal
          isOpen={isOpoContactModalOpen}
          onClose={onOpoContactModalClose}
          onCreated={handlePrimaryContactCreated}
          opoId={getValues('opoId')}
          showInviteAsUser
        />

        <OpoContactInputs
          isClearable
          contactsList={secondaryContactsList}
          disabledContact={disabled?.opoContactId || !hasPermission}
          disabledContactPhone={disabled?.opoSourceContactPhone || !hasPermission}
          opoId={opoId}
          contactFieldName="secondaryOpoContactId"
          phoneFieldName="secondaryOpoContactPhone"
          contactFieldPlaceholder="OPO Contact (Optional)"
          createNewItem={{
            title: 'Create OPO Contact',
            handler: onSecondaryOpoContactModalOpen,
          }}
          onClear={handleClearSecondaryOpo}
        />

        <CreateOpoContactModal
          isOpen={isSecondaryOpoContactModalOpen}
          onClose={onSecondaryOpoContactModalClose}
          onCreated={handleSecondaryContactCreated}
          opoId={getValues('opoId')}
          showInviteAsUser
        />
      </div>
    </FormRow>
  );
});

OpoSelectors.displayName = 'OpoSelectors';
